import * as moment from 'moment';

import { ServiceSummaryTicketType, SummaryTicketFilters, TicketFilter } from '@app/services/tickets.service';
import { TicketCompletedStatusType } from '@shared/enums/ticket-status-type';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUpdateInspectionTicket } from '@shared/interfaces/ticket.interface';
import { RestTicketModel } from '@shared/interfaces/turnover.interface';
import { getFilterDateRange } from '@template/utils/filter';

export namespace TicketListConstants {
  export enum TimeFrame {
    Week = 1,
    Month,
    Month_3,
    Month_6,
    Year,
    AllTime,
    Custom,
  }

  export const timeFrames = [
    { value: TimeFrame.Week, label: 'past 7 days' },
    { value: TimeFrame.Month, label: 'past 30 days' },
    { value: TimeFrame.Month_3, label: 'past 3 months' },
    { value: TimeFrame.Month_6, label: 'past 6 months' },
    { value: TimeFrame.Year, label: 'past year' },
    { value: TimeFrame.AllTime, label: 'all time' },
    { value: TimeFrame.Custom, label: 'custom' },
  ];
}

export const labelConfig: IRadioButtonOption<ServiceSummaryTicketType>[] = [
  {
    label: 'All Tickets',
    value: ServiceSummaryTicketType.AllTickets,
    groupValue: null,
  },
  {
    label: 'Service Tickets',
    value: ServiceSummaryTicketType.ServiceTickets,
    groupValue: 'ticketType',
  },
  {
    label: 'Resident Tickets',
    value: ServiceSummaryTicketType.ResidentTickets,
    groupValue: 'ticketType',
  },
  {
    label: 'Inspection Tickets',
    value: ServiceSummaryTicketType.InspectionTickets,
    groupValue: 'ticketType',
  },
  {
    label: 'Replacement Tickets',
    value: ServiceSummaryTicketType.ReplacementTickets,
    groupValue: 'ticketType',
  },
];

export function getTicketId(ticket?: RestTicketModel | RestUpdateInspectionTicket | undefined): string {
  if (!ticket) return '';

  const pmsTicketText = ticket?.pmsWorkOrderNumber ?? ticket?.pmsWorkOrderId;

  return pmsTicketText ? pmsTicketText : `PU${ticket.id}`;
}

export const getServerPartFilter = (
  filter: { [key: string]: any },
  summaryFilters: Partial<SummaryTicketFilters>,
  currentDate: Date,
  selectedPropertyIds: number[]
): TicketFilter => {
  const filterData: Partial<TicketFilter> = {
    portfolioId: filter.portfolioId,
    includeCompleted: !filter.isCompleted ? null : filter.isCompleted !== TicketCompletedStatusType.IsCompleted,
    propertyIds: selectedPropertyIds?.length ? selectedPropertyIds : null,
  };
  if (filter.timeframe !== TicketListConstants.TimeFrame.Custom)
    filterData.startDate = (getFilterDateRange(currentDate, filter, true)?.startDate as string) || null;

  if (summaryFilters && Object.keys(summaryFilters)?.length) {
    if (filter.timeframe !== TicketListConstants.TimeFrame.Custom)
      filterData.startDate =
        (getFilterDateRange(currentDate, summaryFilters as any, true)?.startDate as string) || null;

    if (summaryFilters.propertyIds) filterData.propertyIds = [summaryFilters.propertyIds];

    if (summaryFilters.collectionId) filterData.collectionIds = summaryFilters.collectionId;

    if (summaryFilters.boardId) filterData.boardId = summaryFilters.boardId;

    if (summaryFilters.unitTypeId) filterData.unitType = summaryFilters.unitTypeId;

    if (summaryFilters.ticketCategoryId) filterData.ticketCategoryId = summaryFilters.ticketCategoryId;

    if (summaryFilters.topicId) filterData.ticketTopicId = summaryFilters.topicId;

    if (summaryFilters.ticketTypeId !== undefined) filterData.ticketSummaryType = summaryFilters.ticketTypeId;
  }

  if (filter.timeframe === TicketListConstants.TimeFrame.Custom) {
    if (filter.customStartDate) filterData.startDate = moment(filter.customStartDate).format('YYYY-MM-DD');
    if (filter.customEndDate) filterData.endDate = moment(filter.customEndDate).format('YYYY-MM-DD');
  }
  return filterData;
};

export type ServiceFilterPanel = {
  field: string;
  text: string;
  value: string | number;
  color?: number;
  isArray: boolean;
  selector: string;
};

export const filterPanelNotEmpty = (filterPanels: { [key: string]: any } | null): boolean => {
  if (!filterPanels) return false;

  return Object.values(filterPanels).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== '';
  });
};

export const applyFilterToQueryParams = (
  data: Record<string, any>,
  queryParams: { [key: string]: any }
): { [key: string]: any } => {
  Object.entries(data).forEach(([key, value]) => {
    if (queryParams.hasOwnProperty(key)) {
      queryParams[key] = Array.isArray(value) ? `[${value}]` : `${value}`;
    }
  });
  return queryParams;
};
